import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit,
  HostListener,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  SearchService,
  SearchResult,
} from 'src/app/services/search/search.service';
import {
  SearchScope,
  getSearchAvatarLabel,
  getSearchBadgeLabel,
  getSearchEntityType,
  getSearchImageUrl,
  getSearchPrimaryLabel,
  getSearchSecondaryLabel,
} from 'src/app/services/search/search.helpers';

const SEARCH_LOGO_COLORS = [
  '#e8f0ff',
  '#fff0f5',
  '#f0f4ff',
  '#fff8ed',
  '#f0fdf4',
  '#fdf4ff',
  '#fef9f0',
  '#f5f5f5',
];

// Routes where the header starts transparent (over a hero) until scrolled
const TRANSPARENT_HEADER_ROUTES = ['/', '/home', '/company-home', '/job-post'];

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  userLang = '';
  userImage: string | null = null;
  userName: string = '';
  userTitle: string = '';
  accessToken: string | null = null;
  userInitials: string = '';
  isCompany: boolean = false;
  searchQuery: string = '';
  isScrolled: boolean = false;
  isHomePage: boolean = true;
  currentUrl: string = '';

  // Search functionality
  searchResults: SearchResult[] = [];
  searchTotalElements = 0;
  showSearchResults: boolean = false;
  searchLoading: boolean = false;
  searchError: string = '';
  // Controls how clicked results are routed, not which endpoint is queried.
  searchScope: SearchScope = 'public';

  // Controls the services dropdown toggle on mobile
  servicesOpen = false;
  profileMenuOpen = false;

  private subscriptions = new Subject<void>();
  private searchQuery$ = new Subject<string>();

  /** Lets the CSS target company mode: app-header.company-header .header { ... } */
  @HostBinding('class.company-header')
  get companyHeaderClass(): boolean {
    return this.isCompany;
  }

  /**
   * Capture-phase scroll handler. Scroll events don't bubble, so a capture
   * listener on window is the only way to also catch scrolling that happens
   * inside a layout container (e.g. company pages with an inner scroll area).
   */
  private readonly onAnyScroll = (event: Event): void => {
    const target = event.target as HTMLElement | Document | null;

    // Ignore scrolling inside the header itself (search dropdown, mobile menu)
    if (
      target instanceof HTMLElement &&
      target.closest &&
      target.closest('.header')
    ) {
      return;
    }

    this.updateScrollState(target);
  };

  constructor(
    private router: Router,
    private localizationService: LocalizationService,
    private userService: UserService,
    private searchService: SearchService,
  ) {
    this.userLang =
      this.localizationService.getUserLang() === 'ar' ? 'en' : 'ar';
  }

  @ViewChild('menu_icon') menu!: ElementRef;
  @ViewChild('close_icon') close!: ElementRef;
  @ViewChild('header') header!: ElementRef;
  @ViewChildren('tab_item') tabItem!: QueryList<ElementRef>;

  ngOnInit() {
    this.currentUrl = this.router.url;
    this.isHomePage = this.isTransparentRoute(this.router.url);

    // Compute the scroll state immediately (page may already be scrolled)
    this.updateScrollState();

    // Listen for scrolling on window AND on inner scroll containers
    window.addEventListener('scroll', this.onAnyScroll, true);

    // Track route changes to know if we're on home page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.subscriptions),
      )
      .subscribe((event: any) => {
        this.currentUrl = event.urlAfterRedirects;
        this.isHomePage = this.isTransparentRoute(event.urlAfterRedirects);
        // Reset scroll state on navigation
        this.updateScrollState();
        this.searchScope = this.getRouteScope();
      });

    this.updateUserData();

    this.userService
      .getUserInfo$()
      .pipe(takeUntil(this.subscriptions))
      .subscribe((userInfo) => {
        this.updateUserDataFromUserInfo(userInfo);
      });

    this.userService
      .getLoginStatus$()
      .pipe(takeUntil(this.subscriptions))
      .subscribe((isLoggedIn) => {
        this.accessToken = isLoggedIn ? this.userService.getAcessToken() : null;
        if (isLoggedIn) {
          this.updateUserData();
        } else {
          this.isCompany = false;
          this.userName = '';
          this.userImage = null;
          this.userInitials = '';
          this.userTitle = '';
        }
      });

    // Setup live search with debounce
    this.searchQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.subscriptions),
      )
      .subscribe((query) => {
        this.performLiveSearch(query);
      });
  }

  ngAfterViewInit() {
    this.tabItem.forEach((link: any) => {
      link.nativeElement.addEventListener('click', () => {
        this.header.nativeElement.classList.add('d-none');
      });
    });
    this.menu.nativeElement.addEventListener('click', () => {
      this.header.nativeElement.classList.remove('d-none');
      this.servicesOpen = false;
    });
    this.close.nativeElement.addEventListener('click', () => {
      this.header.nativeElement.classList.add('d-none');
      this.servicesOpen = false;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target?.closest('.user-profile-menu')) {
      this.profileMenuOpen = false;
    }

    // Close search results when clicking outside
    // (moved here from ngAfterViewInit, where the listener was never removed)
    if (!target?.closest('.search-wrapper-container')) {
      this.showSearchResults = false;
    }
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onAnyScroll, true);
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  /** True when the URL (ignoring query string / hash) is a transparent-header route */
  private isTransparentRoute(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    const normalized =
      path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    return TRANSPARENT_HEADER_ROUTES.includes(normalized);
  }

  /** Reads scroll position from window and, if given, the element that scrolled */
  private updateScrollState(target?: EventTarget | null): void {
    const windowTop =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    let containerTop = 0;
    if (target instanceof HTMLElement) {
      containerTop = target.scrollTop;
    }

    this.isScrolled = Math.max(windowTop, containerTop) > 10;
  }

  /** Toggle services dropdown on mobile */
  toggleServices(): void {
    this.servicesOpen = !this.servicesOpen;
  }

  toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  goToPersonalData(): void {
    this.closeMobileMenu();
    this.profileMenuOpen = false;
    this.router.navigate(['/profile/personal-data']);
  }

  goToProfessionalProfile(): void {
    this.closeMobileMenu();
    this.profileMenuOpen = false;
    this.router.navigate(['/user-profile']);
  }

  logout(): void {
    this.closeMobileMenu();
    this.userService.deleteTokenInfo();
    this.userService.deleteUserInfo();
    this.profileMenuOpen = false;
    this.router.navigate(['/']);
  }

  /** Live search input handler */
  onSearchInput(query: string): void {
    this.searchQuery = query;
    if (query.trim().length >= 2) {
      this.searchScope = this.getRouteScope();
      this.searchQuery$.next(query.trim());
      this.showSearchResults = true;
    } else {
      this.showSearchResults = false;
      this.searchResults = [];
      this.searchTotalElements = 0;
    }
  }

  /** Perform live search */
  private performLiveSearch(query: string): void {
    if (query.length < 2) {
      this.searchResults = [];
      return;
    }

    this.searchLoading = true;
    this.searchError = '';
    this.searchScope = this.getRouteScope();

    const searchFn = this.searchService.searchPublic(query, 0, 6);

    searchFn.pipe(takeUntil(this.subscriptions)).subscribe(
      (response) => {
        const normalized = this.searchService.normalizeResponse(response);
        this.searchResults = (normalized.content || []).slice(0, 6);
        this.searchTotalElements = normalized.totalElements || 0;
        this.searchLoading = false;

        if (this.searchResults.length === 0) {
          this.searchError = 'No results found';
        }
      },
      (error) => {
        console.error('Search error:', error);
        this.searchError = 'Search failed';
        this.searchLoading = false;
        this.searchResults = [];
        this.searchTotalElements = 0;
      },
    );
  }

  /** Select search result */
  selectSearchResult(result: SearchResult): void {
    this.searchQuery = '';
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchTotalElements = 0;

    this.navigateSearchResult(result);
  }

  private navigateSearchResult(result: SearchResult): void {
    const entityType = getSearchEntityType(result);
    const route = this.resolveSearchRoute(result, entityType);

    if (route) {
      this.router.navigate(route);
    }
  }

  private resolveSearchRoute(
    result: SearchResult,
    entityType: string,
  ): any[] | null {
    const explicitRoute =
      result.route ||
      (this.searchScope === 'admin' ? result.adminRoute : result.publicRoute);
    if (explicitRoute) {
      const normalizedRoute = explicitRoute.startsWith('/')
        ? explicitRoute
        : `/${explicitRoute}`;
      return [normalizedRoute];
    }

    if (this.searchScope === 'admin') {
      if (entityType === 'JOB') {
        return ['/company-admin/edit-job', result.id];
      }

      if (entityType === 'COMPANY') {
        return ['/company-admin/company-profile'];
      }

      if (entityType === 'USER') {
        return ['/company-admin/application', result.id];
      }
    } else {
      if (entityType === 'JOB') {
        return ['/job-description', result.id];
      }

      if (entityType === 'COMPANY') {
        return ['/company-profile', result.companyId || result.id];
      }

      if (entityType === 'USER') {
        return ['/profile'];
      }
    }

    return null;
  }

  /** Full search handler (on Enter key) */
  onSearch(): void {
    if (!this.searchQuery.trim()) return;
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchTotalElements = 0;
    this.router.navigate(['/search'], {
      queryParams: {
        q: this.searchQuery.trim(),
        scope: this.getRouteScope(),
      },
    });
    this.searchQuery = '';
    this.closeMobileMenu();
  }

  /** Get icon for result type */
  getResultIcon(type?: string): string {
    switch (type?.toUpperCase()) {
      case 'JOB':
      case 'JOB_POST':
        return 'bi bi-briefcase';
      case 'COMPANY':
        return 'bi bi-building';
      case 'USER':
        return 'bi bi-person';
      default:
        return 'bi bi-search';
    }
  }

  getSearchResultsPreview(): SearchResult[] {
    return this.searchResults.slice(0, 6);
  }

  getSearchResultTitle(result: SearchResult): string {
    return getSearchPrimaryLabel(result);
  }

  getSearchResultSubtitle(result: SearchResult): string {
    return getSearchSecondaryLabel(result);
  }

  getSearchResultBadge(result: SearchResult): string {
    return getSearchBadgeLabel(result);
  }

  getSearchResultAvatarLabel(result: SearchResult): string {
    return getSearchAvatarLabel(result);
  }

  getSearchResultImage(result: SearchResult): string {
    return getSearchImageUrl(result);
  }

  getSearchResultLogoBackground(index: number): string {
    return SEARCH_LOGO_COLORS[index % SEARCH_LOGO_COLORS.length];
  }

  /** Navigate to company dashboard */
  goToDashboard(): void {
    this.closeMobileMenu();
    this.router.navigate(['/company-admin']);
  }

  private updateUserData() {
    this.accessToken = this.userService.getAcessToken();
    const userInfo = this.userService.getUserInfo();
    this.updateUserDataFromUserInfo(userInfo);
  }

  private updateUserDataFromUserInfo(userInfo: any) {
    if (userInfo) {
      this.userName = userInfo.name || '';
      this.userImage = this.userService.getUserImage();
      this.userInitials = this.userService.getUserInitials();

      const roleRaw = userInfo?.role || localStorage.getItem('role');
      const roleName =
        typeof roleRaw === 'object' && roleRaw !== null
          ? roleRaw.name
          : roleRaw;
      this.isCompany = roleName?.toUpperCase() === 'COMPANY';
    } else {
      this.userName = '';
      this.userTitle = '';
      this.userImage = '';
      this.userInitials = '';
      this.isCompany = false;
    }
  }

  changeLang() {
    this.localizationService.setUserLang(this.userLang);
    this.userLang =
      this.localizationService.getUserLang() === 'ar' ? 'en' : 'ar';
    this.header.nativeElement.classList.add('d-none');
  }

  goToProfile() {
    this.closeMobileMenu();
    this.router.navigate(['/profile']);
  }


  getUserInitials(): string {
    return this.userService.getUserInitials();
  }

  closeMobileMenu() {
    this.servicesOpen = false;
    this.profileMenuOpen = false;
    this.header.nativeElement.classList.add('d-none');
  }

  private getRouteScope(): SearchScope {
    return this.router.url.includes('/company-admin') ? 'admin' : 'public';
  }

  /**
   * True when the header should show company-mode nav:
   * - logged-in company user, OR
   * - guest browsing the company landing pages (/company-home, /job-post)
   */
  get isCompanyPage(): boolean {
    if (this.isCompany) return true;
    if (this.currentUrl?.includes('type=company')) return true;
    
    // If we're on login or signup without a query param, check the redirectUrl
    if (this.currentUrl?.includes('/login') || this.currentUrl?.includes('/sign-up')) {
      const redirectUrl = localStorage.getItem('redirectUrl') || '';
      if (redirectUrl.includes('/company-home') || redirectUrl.includes('/job-post') || redirectUrl.includes('/virtual-interview-company')) {
        return true;
      }
    }
    
    const path = (this.currentUrl || '').split('?')[0].split('#')[0];
    return path === '/company-home' || path === '/job-post' || path === '/virtual-interview-company';
  }

  /** True when a guest (not logged in) is on specific landing pages */
  get showSignUpButton(): boolean {
    if (this.accessToken) return false;
    const path = (this.currentUrl || '').split('?')[0].split('#')[0];
    return path === '/home' || path === '/company-home' || path === '/virtual-interview-company';
  }

  get signUpType(): string {
    if (this.currentUrl?.includes('type=company')) return 'company';
    const path = (this.currentUrl || '').split('?')[0].split('#')[0];
    return (path === '/company-home' || path === '/virtual-interview-company') ? 'company' : 'user';
  }

  /** Navigate to sign-up with the appropriate type */
  goToSignUp(): void {
    this.closeMobileMenu();
    this.router.navigate(['/sign-up'], { queryParams: { type: this.signUpType } });
  }
}
